/**
 * Geometry Processing Panel
 *
 * Main panel component that provides access to dissolve, simplify,
 * and geometry calculator tools.
 *
 * **Validates: Requirements 50.1, 51.1, 52.1**
 */

import React, { useCallback } from 'react';
import {
  Box,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Typography,
  Divider,
} from '@mui/material';
import MergeIcon from '@mui/icons-material/MergeType';
import SimplifyIcon from '@mui/icons-material/Timeline';
import CalculateIcon from '@mui/icons-material/Calculate';
import { useSelector, useDispatch } from 'react-redux';
import type { RootState, AppDispatch } from '../../store';
import {
  setActiveTool,
  type GeometryProcessingTool,
} from '../../store/slices/geometryProcessingSlice';
import { DissolveDialog } from './DissolveDialog';
import { SimplifyDialog } from './SimplifyDialog';
import { GeometryCalculatorDialog } from './GeometryCalculatorDialog';
import { geometryProcessor } from '../../services/GeometryProcessor';
import type { GeometryProcessorFeature } from '../../services/GeometryProcessor';
import {
  setProcessing,
  setError,
  setMessage,
  closeTool,
  addAutoUpdateLayer,
} from '../../store/slices/geometryProcessingSlice';

export const GeometryProcessingPanel: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const { activeTool } = useSelector((state: RootState) => state.geometryProcessing);
  const { dissolveTool, simplifyTool, calculatorTool } = useSelector(
    (state: RootState) => state.geometryProcessing
  );
  const layers = useSelector((state: RootState) => state.layers.layers);

  const handleToolSelect = useCallback(
    (tool: GeometryProcessingTool) => {
      dispatch(setActiveTool(tool));
    },
    [dispatch]
  );

  const getLayerFeatures = useCallback(
    (layerId: string): GeometryProcessorFeature[] => {
      const layer = layers.find((l) => l.id === layerId);
      if (!layer?.features) return [];
      return layer.features.map((f) => ({
        id: f.id,
        geometry: f.geometry,
        properties: f.properties || {},
      }));
    },
    [layers]
  );

  const handleDissolveExecute = useCallback(() => {
    if (!dissolveTool.sourceLayerId || !dissolveTool.dissolveField) return;

    dispatch(setProcessing(true));
    dispatch(setError(null));

    try {
      const features = getLayerFeatures(dissolveTool.sourceLayerId);
      const result = geometryProcessor.dissolveFeatures(features, {
        dissolveField: dissolveTool.dissolveField,
        aggregations: dissolveTool.aggregations,
      });

      if (result.success) {
        dispatch(setMessage(result.message || 'Dissolve completed successfully'));
      } else {
        dispatch(setError(result.message || 'Dissolve operation failed'));
      }
    } catch (err) {
      dispatch(setError(err instanceof Error ? err.message : 'Unknown error'));
    } finally {
      dispatch(setProcessing(false));
    }
  }, [dispatch, dissolveTool, getLayerFeatures]);

  const handleSimplifyExecute = useCallback(() => {
    if (!simplifyTool.sourceLayerId) return;

    dispatch(setProcessing(true));
    dispatch(setError(null));

    try {
      const features = getLayerFeatures(simplifyTool.sourceLayerId);
      const result = geometryProcessor.simplifyGeometries(features, {
        tolerance: simplifyTool.tolerance,
        highQuality: simplifyTool.highQuality,
      });

      if (result.success) {
        dispatch(setMessage(result.message || 'Simplification completed successfully'));
      } else {
        dispatch(setError(result.message || 'Simplify operation failed'));
      }
    } catch (err) {
      dispatch(setError(err instanceof Error ? err.message : 'Unknown error'));
    } finally {
      dispatch(setProcessing(false));
    }
  }, [dispatch, simplifyTool, getLayerFeatures]);

  const handleCalculatorExecute = useCallback(() => {
    if (!calculatorTool.sourceLayerId) return;

    dispatch(setProcessing(true));
    dispatch(setError(null));

    try {
      const features = getLayerFeatures(calculatorTool.sourceLayerId);
      const result = geometryProcessor.calculateGeometryProperties(features, {
        properties: calculatorTool.selectedProperties,
        lengthUnit: calculatorTool.lengthUnit,
        areaUnit: calculatorTool.areaUnit,
      });

      if (result.success) {
        dispatch(setMessage(result.message || 'Calculation completed successfully'));
        // Track layer for auto-update if enabled
        if (calculatorTool.autoUpdate) {
          dispatch(addAutoUpdateLayer(calculatorTool.sourceLayerId));
        }
      } else {
        dispatch(setError(result.message || 'Calculation failed'));
      }
    } catch (err) {
      dispatch(setError(err instanceof Error ? err.message : 'Unknown error'));
    } finally {
      dispatch(setProcessing(false));
    }
  }, [dispatch, calculatorTool, getLayerFeatures]);

  return (
    <Box>
      <Typography variant="subtitle1" sx={{ px: 2, py: 1, fontWeight: 600 }}>
        Geometry Processing
      </Typography>
      <Divider />
      <List>
        <ListItemButton onClick={() => handleToolSelect('dissolve')}>
          <ListItemIcon>
            <MergeIcon />
          </ListItemIcon>
          <ListItemText primary="Dissolve" secondary="Merge features by attribute value" />
        </ListItemButton>

        <ListItemButton onClick={() => handleToolSelect('simplify')}>
          <ListItemIcon>
            <SimplifyIcon />
          </ListItemIcon>
          <ListItemText primary="Simplify" secondary="Reduce geometry complexity" />
        </ListItemButton>

        <ListItemButton onClick={() => handleToolSelect('calculator')}>
          <ListItemIcon>
            <CalculateIcon />
          </ListItemIcon>
          <ListItemText
            primary="Geometry Calculator"
            secondary="Calculate area, length, coordinates"
          />
        </ListItemButton>
      </List>

      {/* Dialogs */}
      <DissolveDialog open={activeTool === 'dissolve'} onExecute={handleDissolveExecute} />
      <SimplifyDialog open={activeTool === 'simplify'} onExecute={handleSimplifyExecute} />
      <GeometryCalculatorDialog
        open={activeTool === 'calculator'}
        onExecute={handleCalculatorExecute}
      />
    </Box>
  );
};

export default GeometryProcessingPanel;
